/**
 * Lightweight image caching utility using IndexedDB.
 * Stores thumbnail images as blobs in the browser so they
 * survive page refreshes without re-downloading from the server.
 */

const DB_NAME = 'boss-cushions-cache';
const STORE_NAME = 'images';
const DB_VERSION = 1;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

async function getCachedBlob(url) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(url);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function setCachedBlob(url, blob) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(blob, url);
  } catch {
    // Silently fail - caching is best-effort
  }
}

// In-memory URL map for instant access within same session
const memoryCache = new Map();

/**
 * Returns a blob URL for the given image path.
 * First checks in-memory cache, then IndexedDB, then fetches from network.
 */
export async function getCachedImageUrl(imagePath) {
  // Already in memory this session
  if (memoryCache.has(imagePath)) {
    return memoryCache.get(imagePath);
  }

  // Try IndexedDB (survives refresh)
  const cached = await getCachedBlob(imagePath);
  if (cached) {
    const blobUrl = URL.createObjectURL(cached);
    memoryCache.set(imagePath, blobUrl);
    return blobUrl;
  }

  // Fetch from network and cache
  try {
    const response = await fetch(imagePath);
    if (!response.ok) return imagePath;
    const blob = await response.blob();
    await setCachedBlob(imagePath, blob);
    const blobUrl = URL.createObjectURL(blob);
    memoryCache.set(imagePath, blobUrl);
    return blobUrl;
  } catch {
    return imagePath; // Fallback to original URL
  }
}

/**
 * Preload and cache a list of image URLs.
 * Call this on app startup for instant product grid rendering.
 */
export async function preloadImages(urls) {
  await Promise.all(urls.map(url => getCachedImageUrl(url)));
}
